<template>
  <div class="tw-prose__features tw-prose">
    <div ref="lightboxRef">
      <component :is="{ render: () => nodes.map(getVNode) }" />
    </div>
  </div>
</template>

<script setup>
import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import { computed, h, onMounted, useTemplateRef } from "vue";
import { RouterLink } from "vue-router";

const props = defineProps({
  content: {
    type: String,
    default: "",
  },
});

// Helper to check if URL is internal and extract path
const getInternalPath = (url) => {
  if (!url) {
    return null;
  }

  // Already a relative path
  if (url.startsWith("/") || url.startsWith("#") || url.startsWith("?")) {
    return url;
  }

  // Check if it's one of our known domains
  const internalDomains = [
    "https://tool.catwise.nl",
    "https://tool-test.catwise.nl",
    "http://localhost:8080",
    "http://localhost:5173",
  ];

  for (const domain of internalDomains) {
    if (url.startsWith(domain)) {
      try {
        const urlObj = new URL(url);
        return urlObj.pathname + urlObj.search + urlObj.hash;
      } catch {
        return null;
      }
    }
  }

  // Check if URL matches current origin
  try {
    const urlObj = new URL(url);
    if (urlObj.origin === window.location.origin) {
      return urlObj.pathname + urlObj.search + urlObj.hash;
    }
  } catch {
    // Not a valid URL
  }

  return null;
};

// Template ref
const lightboxRef = useTemplateRef("lightboxRef");

const LIGHTBOX_NODE_TYPES = ["img"];

const parseNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const tag = node.tagName.toLowerCase();
  let component = tag;

  const props = {};
  for (const attr of node.attributes) {
    props[attr.name] = attr.value;
  }

  // Handle anchor tags - use router-link for internal URLs
  if (tag === "a" && props.href) {
    const internalPath = getInternalPath(props.href);

    if (internalPath) {
      // Internal link - use router-link
      component = RouterLink;
      props.to = internalPath;
      delete props.href;
      delete props.target;
      delete props.rel;
    } else {
      // External link - add target="_blank" and rel for security
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    }
  }

  if (LIGHTBOX_NODE_TYPES.includes(tag)) {
    props["data-lightbox"] = props.src;
    props["data-external-thumb-image"] = props.src;

    // Classes
    props.class = "tw-cursor-pointer";
  }

  // Handle <pre> tags that contain iframe HTML
  if (tag === "pre") {
    const textContent = node.textContent || "";
    const iframeMatch = textContent.match(/<iframe[^>]*>.*?<\/iframe>/i);

    if (iframeMatch) {
      // Parse the iframe HTML
      const tempParser = new DOMParser();
      const iframeDoc = tempParser.parseFromString(iframeMatch[0], "text/html");
      const iframeElement = iframeDoc.querySelector("iframe");

      if (iframeElement) {
        const iframeProps = {};
        for (const attr of iframeElement.attributes) {
          iframeProps[attr.name] = attr.value;
        }

        // Add responsive styling for iframe
        iframeProps.class =
          (iframeProps.class || "") + " tw-w-full tw-aspect-video";
        iframeProps.style =
          (iframeProps.style || "") + "; max-width: 100%; height: auto;";

        return {
          component: "iframe",
          props: iframeProps,
          children: [],
        };
      }
    }
  }

  const children = [];
  node.childNodes.forEach((child) => {
    const parsed = parseNode(child);

    if (parsed !== null && parsed !== undefined && parsed !== "") {
      children.push(parsed);
    }
  });

  return { component, props, children };
};

const getVNode = (node) => {
  if (typeof node === "string") {
    return node;
  }

  if (!node) {
    return null;
  }

  return h(
    node.component,
    node.props,
    node.children ? node.children.map(getVNode) : undefined,
  );
};

const nodes = computed(() => {
  if (!props.content) {
    return [];
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(props.content, "text/html");

  return Array.from(parsed.body.childNodes).map(parseNode).filter(Boolean);
});

onMounted(() => {
  lightGallery(lightboxRef.value, {
    licenseKey: import.meta.env?.VITE_LIGHTGALLERY_LICENSE_KEY,
    plugins: [lgThumbnail, lgZoom],
    speed: 350,
    selector: "[data-lightbox]",
    exThumbImage: "data-external-thumb-image",
    addClass: "light-gallery",
  });
});
</script>
